// Browser drafts for one form, wired to one record.
//
// The storage rules (ownership stamp, 7-day retention, typed values only) live in
// utils/formDraftStorage.ts; this is the per-form glue: read what exists, autosave
// what the user types, and hand the screen an explicit restore/discard pair.
//
// Two deliberate non-behaviours:
//   - a draft is NEVER applied on its own. The form shows a banner and the user
//     clicks Restore. Silently resurrecting old input into a form somebody is
//     about to save is how wrong data gets written.
//   - a draft is NEVER created just by opening a form. The first watch firing is
//     the form initializing itself (or a restore being applied), so it is skipped;
//     only input the user actually made is parked.
//
// The deliberate-close rule (ADR 0069, amended 2026-09-17, #501): a form the user
// closes on purpose (Abbrechen, ×, Escape, a backdrop click) is a decision, not an
// interruption — unlike navigating away, reloading, or losing the tab, all of
// which keep the draft for the banner to offer back. `confirmClose()` is how a
// host makes that decision: no unsaved input, nothing to ask, close proceeds;
// unsaved input, ask once, and a confirmed discard clears the draft for good.
//
// The base rule (ADR 0101, amending ADR 0069 rule 5): a form that edits a shared
// record passes a `baseline` — the record as it loaded it, and its version
// (`updatedAt`). The draft then stores ONLY the fields the user changed against
// that baseline, plus the version. Restore puts back only those fields, over
// whatever the record holds now, so a colleague's newer values survive in every
// field this user never touched; and when the record moved since the draft was
// taken, `recordChangedSinceDraft` lets the banner say so. The same version is
// what the save sends as `expectedUpdatedAt`, and a save the server refuses as
// `RECORD_CHANGED` keeps the input (`noteSaveFailed`) for a Reload + Restore.

import { computed, onBeforeUnmount, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useConfirm } from './useDialog';
import { t, te } from '../i18n';
import { changedFields } from '../utils/changedFields';
import { isRecordChanged } from '../utils/apiError';
import {
  discardDraft,
  readDraft,
  writeDraft,
  type FormDraft,
  type JsonValue,
} from '../utils/formDraftStorage';

/** The record a form loaded, as the draft compares against it (ADR 0101). */
export interface FormDraftBaseline {
  /**
   * The record's `updatedAt` as loaded (an ISO string), or `null` when there is
   * no record yet (an upsert singleton before its first save). A host whose form
   * spans two records joins both versions into one string.
   */
  version: string | null;
  /** The loaded record, projected exactly like `values()` projects the form. */
  values: Record<string, JsonValue>;
}

// The form's own display name ONLY — see `formDraftStorage.ts`'s own note (via
// `listDraftsForOwner`) on why a record id must never reach a dialog body. A
// form id with no catalogue entry falls back to the raw id rather than an
// empty line.
function formDisplayName(formId: string): string {
  const key = `formDrafts.forms.${formId}`;
  return te(key) ? t(key) : formId;
}

export interface UseFormDraftOptions {
  /** Current tenant slug (getter — the store value may arrive after setup). */
  tenantSlug: () => string | null | undefined;
  /** Signed-in master user id — the owner stamp (getter, same reason). */
  userId: () => string | null | undefined;
  /** Stable identifier of this form, e.g. `time.project`. */
  formId: string;
  /** Entity id being edited, or `'new'` for a record that does not exist yet. */
  recordId: () => string;
  /**
   * The form's typed field values. A getter rather than the form ref itself, so a
   * screen can project its state down to plain serializable data (dropping files,
   * DOM refs, picker objects) instead of handing the whole reactive form over.
   */
  values: () => Record<string, JsonValue>;
  /** Autosave debounce; the default is fine for typing. */
  debounceMs?: number;
  /**
   * The record as the form loaded it (ADR 0101), or null while it has not
   * loaded. Pass it from every form that edits a SHARED record: the draft then
   * holds only the user's changed fields plus the record's version, Restore
   * applies only those, and `expectedUpdatedAt()` is the version to send.
   * Omitted (create forms, per-user records): drafts keep every field, as before.
   */
  baseline?: () => FormDraftBaseline | null | undefined;
}

export interface FormDraftController {
  /** A restorable draft exists for the current record. */
  hasDraft: Ref<boolean>;
  /** When that draft was written (epoch ms), or null. */
  draftSavedAt: Ref<number | null>;
  /**
   * Input has been made since the form last primed: the values watch fired
   * after the initial load (typed input), or `restore()` applied a draft this
   * session. A draft the user never restored does not count — see
   * `confirmClose()`.
   */
  hasUnsavedInput: Ref<boolean>;
  /**
   * Apply the stored values through the caller's own setter and hide the banner.
   * With a baseline, `applyFn` receives the form's CURRENT values with only the
   * draft's changed fields put over them — so a setter that replaces the whole
   * form still leaves every other field as the record holds it now.
   */
  restore: (applyFn: (values: Record<string, JsonValue>) => void) => void;
  /** The keys the last `restore()` put back (empty without a baseline draft). */
  restoredKeys: Ref<string[]>;
  /**
   * The offered draft was taken against an older version of the record than
   * the one the form holds now — the banner says Restore applies only the
   * user's own fields over a record somebody else changed.
   */
  recordChangedSinceDraft: ComputedRef<boolean>;
  /**
   * The last save was refused because the record changed meanwhile
   * (`RECORD_CHANGED`). The banner offers Reload; cleared by `noteFormReset()`.
   */
  recordChanged: Ref<boolean>;
  /**
   * The value a save sends as `expectedUpdatedAt`: the baseline's version, `null`
   * for "I saw no record", `undefined` without a baseline (the field is then
   * left out of the body, and the server keeps its old behaviour).
   */
  expectedUpdatedAt: () => string | null | undefined;
  /** Park the current input now, without waiting for the autosave debounce. */
  flush: () => void;
  /**
   * Call from the save's failure branch. On `RECORD_CHANGED` the input is parked
   * at once and `recordChanged` is set, so Reload + Restore loses nothing.
   */
  noteSaveFailed: (outcome: unknown) => void;
  /** Drop the draft because the user said so. */
  discard: () => void;
  /**
   * Call when the form re-initializes itself from source data — a modal reopening
   * on the same record, an async reload of the entity. Without it, that reset
   * would be autosaved over the draft it is supposed to offer, and the banner
   * would stay hidden after a reopen.
   */
  noteFormReset: () => void;
  /**
   * Drop the draft because the record was saved for real. Call this from the
   * EXISTING success branch of the form's save handler — the draft has served its
   * purpose the moment the server has the data.
   */
  clearOnSave: () => void;
  /**
   * Call from a deliberate close (Abbrechen, ×, Escape, a backdrop click) —
   * never from navigation, reload, or an idle/forced sign-out, which must keep
   * the draft untouched for the banner. No unsaved input: resolves `true` at
   * once, no dialog. Unsaved input: asks once; declining resolves `false` and
   * leaves the draft as it was, confirming discards it and resolves `true`.
   */
  confirmClose: () => Promise<boolean>;
}

export function useFormDraft(options: UseFormDraftOptions): FormDraftController {
  const debounceMs = options.debounceMs ?? 800;

  const hasDraft = ref(false);
  const draftSavedAt = ref<number | null>(null);
  const hasUnsavedInput = ref(false);
  const restoredKeys = ref<string[]>([]);
  const recordChanged = ref(false);
  // The offered draft's base version: `undefined` for no draft or a legacy draft
  // without one, so a legacy draft never claims the record moved.
  const draftBaseVersion = ref<string | null | undefined>(undefined);

  let timer: ReturnType<typeof setTimeout> | null = null;
  // The next values watch firing is the form loading itself, not user input.
  let primed = false;
  // Set once the record has been saved: nothing more is parked for it.
  let stopped = false;

  function cancelPending() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function currentDraft(): FormDraft | null {
    return readDraft(options.tenantSlug(), options.userId(), options.formId, options.recordId());
  }

  function refresh() {
    const draft = currentDraft();
    hasDraft.value = draft !== null;
    draftSavedAt.value = draft?.savedAt ?? null;
    draftBaseVersion.value = draft?.base ? draft.base.version : undefined;
  }

  function currentBaseline(): FormDraftBaseline | null {
    return options.baseline?.() ?? null;
  }

  function persist() {
    if (stopped) return;
    const baseline = currentBaseline();
    if (!baseline) {
      writeDraft(
        options.tenantSlug(),
        options.userId(),
        options.formId,
        options.recordId(),
        options.values(),
      );
      return;
    }
    const changed = changedFields(options.values(), baseline.values, { alsoKeys: restoredKeys.value });
    if (Object.keys(changed).length === 0) {
      // Typed back to what the record says: nothing of the user's own is left
      // to offer, and an empty draft would only raise a banner with nothing in it.
      discardDraft(options.tenantSlug(), options.formId, options.recordId());
      return;
    }
    writeDraft(
      options.tenantSlug(),
      options.userId(),
      options.formId,
      options.recordId(),
      changed as Record<string, JsonValue>,
      { version: baseline.version },
    );
  }

  function noteFormReset() {
    cancelPending();
    primed = false;
    stopped = false;
    hasUnsavedInput.value = false;
    recordChanged.value = false;
    restoredKeys.value = [];
    refresh();
  }

  const recordChangedSinceDraft = computed(() => {
    if (draftBaseVersion.value === undefined) return false;
    const baseline = options.baseline?.();
    if (!baseline) return false;
    return baseline.version !== draftBaseVersion.value;
  });

  function expectedUpdatedAt(): string | null | undefined {
    const baseline = currentBaseline();
    return baseline ? baseline.version : undefined;
  }

  function flush() {
    cancelPending();
    persist();
  }

  function noteSaveFailed(outcome: unknown) {
    if (!isRecordChanged(outcome)) return;
    flush();
    recordChanged.value = true;
  }

  // A new record (or a new tenant/user) is a different draft slot: re-read it, and
  // treat the form's re-initialization as "not user input" again.
  watch(
    [() => options.tenantSlug(), () => options.userId(), () => options.recordId()],
    noteFormReset,
    { immediate: true },
  );

  watch(
    () => options.values(),
    () => {
      if (!primed) {
        // First firing: the form filling itself in (initForm, a restore, an async
        // reload). Parking that would save a form as a draft of itself.
        primed = true;
        return;
      }
      if (stopped) return;
      // A form re-priming after a session-scoped restore (see `restore()`) has
      // already set this; a genuine keystroke sets it for the first time here.
      hasUnsavedInput.value = true;
      cancelPending();
      timer = setTimeout(() => {
        timer = null;
        persist();
      }, debounceMs);
    },
    { deep: true },
  );

  onBeforeUnmount(() => {
    // Flush what the user typed in the last debounce window — closing the tab
    // mid-sentence is exactly the case this feature exists for. Never after a
    // successful save (`stopped`), or the modal unmounting would resurrect the
    // draft we just deleted.
    if (timer !== null) {
      cancelPending();
      persist();
    }
  });

  function restore(applyFn: (values: Record<string, JsonValue>) => void) {
    const draft = currentDraft();
    if (draft?.base && currentBaseline()) {
      // Only the fields the user changed, over the form as it stands now (the
      // record's current values): a colleague's newer value in any other field
      // is kept, not reverted to what this browser saw days ago.
      restoredKeys.value = Object.keys(draft.values);
      applyFn({ ...options.values(), ...draft.values });
      hasUnsavedInput.value = true;
    } else if (draft) {
      applyFn(draft.values);
      // Applied input, same as if the user had typed it — a close right after
      // a restore must ask, same as a close right after typing would.
      hasUnsavedInput.value = true;
    }
    // The banner is done either way; the draft itself stays in storage until the
    // form is saved or the user discards it, so restoring is not destructive.
    hasDraft.value = false;
  }

  function discard() {
    cancelPending();
    discardDraft(options.tenantSlug(), options.formId, options.recordId());
    hasDraft.value = false;
    draftSavedAt.value = null;
    draftBaseVersion.value = undefined;
    hasUnsavedInput.value = false;
    restoredKeys.value = [];
  }

  // Shared by `clearOnSave()` and a confirmed `confirmClose()`: once the draft
  // is gone for a reason other than the form re-priming, nothing more is
  // parked for it until the next `noteFormReset()` — otherwise a form that
  // stays mounted and resets its own values right after (a close that does not
  // unmount, a save that redisplays a blank form) would autosave that reset
  // straight back into a fresh draft.
  function stopParkingAndDiscard() {
    cancelPending();
    stopped = true;
    discardDraft(options.tenantSlug(), options.formId, options.recordId());
    hasDraft.value = false;
    draftSavedAt.value = null;
    draftBaseVersion.value = undefined;
    hasUnsavedInput.value = false;
    recordChanged.value = false;
    restoredKeys.value = [];
  }

  function clearOnSave() {
    stopParkingAndDiscard();
  }

  async function confirmClose(): Promise<boolean> {
    if (!hasUnsavedInput.value) {
      // Nothing to lose — close proceeds with no dialog, and no pending
      // autosave is left to flush a value on unmount that nobody was warned
      // about keeping.
      cancelPending();
      return true;
    }
    const confirmed = await useConfirm().confirm({
      title: t('formDrafts.closeTitle', { form: formDisplayName(options.formId) }),
      body: t('formDrafts.closeBody'),
      variant: 'warning',
      confirmTone: 'danger',
      confirmLabel: t('formDrafts.closeConfirm'),
      cancelLabel: t('formDrafts.closeCancel'),
    });
    if (!confirmed) return false;
    stopParkingAndDiscard();
    return true;
  }

  return {
    hasDraft,
    draftSavedAt,
    hasUnsavedInput,
    restore,
    restoredKeys,
    recordChangedSinceDraft,
    recordChanged,
    expectedUpdatedAt,
    flush,
    noteSaveFailed,
    discard,
    noteFormReset,
    clearOnSave,
    confirmClose,
  };
}
